import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Plus, Globe, MapPin, Pencil, Check, X, Trash2, Star, ArrowUpRight, Paperclip, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtCompact, fmtNOK, fmtDate, relTime, fileSize } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton, Card, StageBadge, HealthIndicator, SectionTitle, AIButton, Stat, UserAvatar, StatusChip } from "@/components/common";
import { ActivityIcon } from "@/components/opportunity/ActivityTab";
import { cn } from "@/lib/utils";

function ContactDialog({ accountId, contact, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", role: "", email: "", phone: "", is_decision_maker: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: contact?.name || "", role: contact?.role || "", email: contact?.email || "", phone: contact?.phone || "", is_decision_maker: !!contact?.is_decision_maker }); }, [open, contact]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (contact?.id) await api.patch(`/contacts/${contact.id}`, form);
      else await api.post(`/accounts/${accountId}/contacts`, form);
      toast.success(contact?.id ? "Contact updated" : "Contact added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="contact-dialog">
        <DialogHeader><DialogTitle>{contact?.id ? "Edit contact" : "New contact"}</DialogTitle><DialogDescription>Who are we talking to, and who decides?</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-[10px]" required autoFocus data-testid="contact-name-input" /></div>
          <div className="space-y-1.5"><Label>Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-10 rounded-[10px]" data-testid="contact-role-input" /></div>
          <div className="grid grid-cols-2 gap-3"><div className="space-y-1.5"><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 rounded-[10px]" data-testid="contact-email-input" /></div><div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10 rounded-[10px]" data-testid="contact-phone-input" /></div></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.is_decision_maker} onCheckedChange={(v) => setForm({ ...form, is_decision_maker: !!v })} data-testid="contact-decision-maker-checkbox" /> Decision maker</label>
          <div className="flex justify-end gap-2 pt-1"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy || !form.name.trim()} className="bg-primary text-white hover:bg-[#ff8e76]" data-testid="contact-submit-button">{busy ? "Saving..." : "Save"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewOpportunityDialog({ account, open, onOpenChange }) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { if (open) { setName(""); setValue(""); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/opportunities", { name: name.trim(), account_id: account.id, value: Number(value) || 0 });
      toast.success("Opportunity created");
      onOpenChange(false);
      navigate(`/opportunities/${r.data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-opportunity-dialog">
        <DialogHeader><DialogTitle>New opportunity for {account?.name}</DialogTitle><DialogDescription>Tip: pasting an e-mail into 'New inquiry' fills all of this automatically.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Studio 2 upgrade" className="h-10 rounded-[10px]" required autoFocus data-testid="new-opportunity-name-input" /></div>
          <div className="space-y-1.5"><Label>Estimated value (NOK)</Label><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} className="h-10 rounded-[10px] tabular" data-testid="new-opportunity-value-input" /></div>
          <div className="flex justify-end gap-2 pt-1"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy || !name.trim()} className="bg-primary text-white hover:bg-[#ff8e76]" data-testid="new-opportunity-submit-button">{busy ? "Creating..." : "Create"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [contactDlg, setContactDlg] = useState(null); // null | {} | contact
  const [oppDlg, setOppDlg] = useState(false);
  const [editDesc, setEditDesc] = useState(false);
  const [desc, setDesc] = useState("");

  const load = useCallback(() => api.get(`/accounts/${id}`).then((r) => { setData(r.data); setBrief(r.data.account.last_brief || null); }).catch((e) => setError(errMsg(e))), [id]);
  useEffect(() => { setData(null); load(); }, [load]);

  const prepare = useCallback(async () => {
    setBriefBusy(true);
    try {
      const r = await api.post(`/accounts/${id}/brief`);
      setBrief(r.data);
      toast.success("Meeting brief ready");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBriefBusy(false);
    }
  }, [id]);

  useEffect(() => {
    if (params.get("brief") === "1" && data) {
      const p = new URLSearchParams(params);
      p.delete("brief");
      setParams(p, { replace: true });
      prepare();
    }
  }, [params, data, prepare, setParams]);

  if (error) return <div className="mx-auto max-w-3xl px-6 py-20 text-center"><h1 className="text-2xl font-semibold">Customer not found</h1><Button asChild variant="outline" className="mt-4"><Link to="/customers">Back to customers</Link></Button></div>;

  const a = data?.account;
  const open = (data?.opportunities || []).filter((o) => o.status === "open");
  const closed = (data?.opportunities || []).filter((o) => o.status !== "open");

  const saveDesc = async () => {
    try {
      await api.patch(`/accounts/${id}`, { description: desc });
      setEditDesc(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    await api.delete(`/contacts/${c.id}`);
    load();
  };

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
      <Link to="/customers" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="customer-back-link"><ArrowLeft className="h-4 w-4" /> Customers</Link>
      {!a ? (
        <div className="mt-4 space-y-4"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-20" /><Skeleton className="h-64" /></div>
      ) : (
        <>
          <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <h1 className="text-3xl font-semibold tracking-[-0.02em] sm:text-4xl" data-testid="customer-name">{a.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span>{a.industry}</span>
                {(a.city || a.country) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[a.city, a.country].filter(Boolean).join(", ")}</span>}
                {a.website && <a href={`https://${a.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground"><Globe className="h-3.5 w-3.5" /> {a.website}</a>}
                <span className="inline-flex items-center gap-1.5"><UserAvatar name={a.owner_name} /> {a.owner_name}</span>
                <span className="rounded-full border bg-white px-2 py-0.5 text-[11px]">{a.legal_entity} · {a.currency}</span>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button variant="outline" className="gap-2" onClick={() => setOppDlg(true)} data-testid="customer-new-opportunity-button"><Plus className="h-4 w-4" /> Opportunity</Button>
              <AIButton loading={briefBusy} onClick={prepare} className="bg-primary text-white hover:bg-[#ff8e76]" data-testid="customer-prepare-me-button">Prepare me</AIButton>
            </div>
          </div>

          <Card className="mt-6 grid grid-cols-2 gap-6 p-5 sm:grid-cols-4" data-testid="customer-stats">
            <Stat label="Open pipeline" value={fmtCompact(data.stats.open_value)} sub={`${data.stats.open_count} opportunit${data.stats.open_count === 1 ? "y" : "ies"}`} />
            <Stat label="Won revenue" value={fmtCompact(data.stats.won_revenue)} sub={`${data.stats.won_count} won`} />
            <Stat label="Contacts" value={data.contacts.length} sub={`${data.contacts.filter((c) => c.is_decision_maker).length} decision maker${data.contacts.filter((c) => c.is_decision_maker).length === 1 ? "" : "s"}`} />
            <Stat label="Last activity" value={data.activities[0] ? relTime(data.activities[0].date) : "–"} sub={data.activities[0]?.title} />
          </Card>

          {(brief || briefBusy) && (
            <Card className="mt-6 border-primary/25 bg-primary/[0.04] p-5 fade-in" data-testid="customer-brief">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Meeting brief{a.last_brief_at && !briefBusy ? <span className="text-xs font-normal text-muted-foreground">· {relTime(a.last_brief_at)}</span> : null}</div>
                {brief && !briefBusy && <button type="button" onClick={() => setBrief(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
              </div>
              {briefBusy ? <div className="mt-3 space-y-2"><Skeleton className="h-5 w-3/4" /><Skeleton className="h-4 w-1/2" /><Skeleton className="h-16" /></div> : (
                <div className="mt-3 grid grid-cols-1 gap-6 text-sm md:grid-cols-[1.4fr_1fr]">
                  <div className="space-y-4">
                    <p className="text-base font-medium leading-snug" data-testid="customer-brief-headline">{brief.headline}</p>
                    <p className="text-muted-foreground">{brief.last_interaction}</p>
                    <div><div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Talking points</div><ul className="list-disc space-y-1 pl-5">{(brief.talking_points || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                    {brief.suggested_goal && <div className="rounded-xl border bg-white px-4 py-3"><div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">A good outcome</div><div className="mt-0.5">{brief.suggested_goal}</div></div>}
                  </div>
                  <div className="space-y-4">
                    <div><div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Clarify</div><ul className="list-disc space-y-1 pl-5">{(brief.clarify || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                    {brief.risks?.length > 0 && <div><div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Risks</div><ul className="list-disc space-y-1 pl-5">{brief.risks.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
                    {brief.facts && <div className="grid grid-cols-3 gap-3 rounded-xl border bg-white px-4 py-3 text-xs"><div><div className="text-muted-foreground">Open</div><div className="font-medium tabular">{brief.facts.open_opportunities}</div></div><div><div className="text-muted-foreground">Pipeline</div><div className="font-medium tabular">{fmtCompact(brief.facts.total_pipeline)}</div></div><div><div className="text-muted-foreground">Last quote</div><div className="font-medium tabular">{brief.facts.last_quote_value ? fmtCompact(brief.facts.last_quote_value) : "–"}</div></div></div>}
                  </div>
                </div>
              )}
            </Card>
          )}

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr_1fr]">
            <div className="space-y-9">
              <section>
                <SectionTitle right={!editDesc && <button type="button" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => { setDesc(a.description || ""); setEditDesc(true); }} data-testid="customer-edit-description-button"><Pencil className="h-3 w-3" /> Edit</button>}>About</SectionTitle>
                {editDesc ? <div className="space-y-2"><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="rounded-xl text-sm" autoFocus data-testid="customer-description-input" /><div className="flex gap-2"><Button size="sm" onClick={saveDesc} className="gap-1 bg-primary text-white hover:bg-[#ff8e76]" data-testid="customer-description-save-button"><Check className="h-3.5 w-3.5" /> Save</Button><Button size="sm" variant="ghost" onClick={() => setEditDesc(false)}><X className="h-3.5 w-3.5" /></Button></div></div> : <p className="text-sm leading-6" data-testid="customer-description">{a.description || <span className="italic text-muted-foreground">No description yet.</span>}</p>}
              </section>

              <section data-testid="customer-opportunities">
                <SectionTitle right={<span className="text-xs text-muted-foreground">{open.length} open · {closed.length} closed</span>}>Opportunities</SectionTitle>
                {data.opportunities.length === 0 ? <p className="text-sm text-muted-foreground">No opportunities yet.</p> : (
                  <Card className="divide-y">
                    {[...open, ...closed].map((o) => (
                      <Link key={o.id} to={`/opportunities/${o.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-muted/40" data-testid="customer-opportunity-row">
                        <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{o.name}</div><div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><StageBadge stage={o.stage} className="px-2 py-0.5 text-[11px]" />{o.next_step ? <span className="truncate">Next: {o.next_step}</span> : <span className="text-[hsl(var(--status-warn))]">No next step</span>}</div></div>
                        <div className="text-right"><div className="text-sm font-medium tabular">{fmtNOK(o.value)}</div><div className="text-xs text-muted-foreground">{o.status === "open" ? <HealthIndicator health={o.health} reason={o.reason} /> : o.status === "won" ? `Won ${fmtDate(o.won_at)}` : `Lost · ${o.lost_reason}`}</div></div>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </Link>
                    ))}
                  </Card>
                )}
              </section>

              <section data-testid="customer-activity">
                <SectionTitle>Activity</SectionTitle>
                {data.activities.length === 0 ? <p className="text-sm text-muted-foreground">Nothing yet.</p> : (
                  <ul className="space-y-3">{data.activities.slice(0, 12).map((x) => <li key={x.id} className="flex items-start gap-3 text-sm"><ActivityIcon type={x.type} className="mt-0.5" /><div className="min-w-0 flex-1"><div className="flex flex-wrap items-baseline justify-between gap-x-3"><span className="font-medium">{x.title}</span><span className="text-xs tabular text-muted-foreground">{relTime(x.date)}</span></div>{x.detail && <div className="line-clamp-2 text-xs leading-5 text-muted-foreground">{x.detail}</div>}</div></li>)}</ul>
                )}
              </section>
            </div>

            <aside className="space-y-8">
              <section data-testid="customer-contacts">
                <SectionTitle right={<button type="button" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setContactDlg({})} data-testid="customer-add-contact-button"><Plus className="h-3 w-3" /> Add</button>}>Contacts</SectionTitle>
                {data.contacts.length === 0 ? <p className="text-sm text-muted-foreground">No contacts yet.</p> : (
                  <div className="space-y-3">
                    {data.contacts.map((c) => (
                      <div key={c.id} className="group flex items-start gap-3 text-sm" data-testid="customer-contact-row">
                        <UserAvatar name={c.name} color="#6b7280" size="md" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 font-medium">{c.name}{c.is_decision_maker && <Star className="h-3 w-3 fill-primary text-primary" title="Decision maker" />}</div>
                          <div className="text-xs text-muted-foreground">{c.role}</div>
                          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">{c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-foreground"><Mail className="h-3 w-3" />{c.email}</a>}{c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}</div>
                        </div>
                        <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100"><button type="button" onClick={() => setContactDlg(c)} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="customer-contact-edit-button"><Pencil className="h-3.5 w-3.5" /></button><button type="button" onClick={() => removeContact(c)} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive" data-testid="customer-contact-delete-button"><Trash2 className="h-3.5 w-3.5" /></button></div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
              <section data-testid="customer-documents">
                <SectionTitle>Documents</SectionTitle>
                {data.documents.length === 0 ? <p className="text-sm text-muted-foreground">No documents yet.</p> : (
                  <ul className="space-y-2">{data.documents.slice(0, 8).map((d) => <li key={d.id} className="flex items-center gap-2 text-sm"><Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><Link to={`/opportunities/${d.opportunity_id}?tab=files`} className="min-w-0 flex-1 truncate hover:underline underline-offset-4">{d.name}</Link><StatusChip status={d.kind} label={(d.kind || "").replace("_", " ")} /><span className="shrink-0 text-xs text-muted-foreground">{fileSize(d.size)}</span></li>)}</ul>
                )}
              </section>
            </aside>
          </div>

          <ContactDialog accountId={id} contact={contactDlg && contactDlg.id ? contactDlg : null} open={contactDlg !== null} onOpenChange={(v) => !v && setContactDlg(null)} onSaved={load} />
          <NewOpportunityDialog account={a} open={oppDlg} onOpenChange={setOppDlg} />
        </>
      )}
    </div>
  );
}
